(() => {
  'use strict';

  class PDLogoCarousel {
    constructor(section) {
      this.section = section;

      this.viewport = section.querySelector(
        '[data-pd-logo-viewport]'
      );

      this.track = section.querySelector(
        '[data-pd-logo-track]'
      );

      this.items = Array.from(
        section.querySelectorAll(
          '[data-pd-logo-item]'
        )
      );

      this.prevButton = section.querySelector(
        '[data-pd-logo-prev]'
      );

      this.nextButton = section.querySelector(
        '[data-pd-logo-next]'
      );

      this.dots = Array.from(
        section.querySelectorAll(
          '[data-pd-logo-dot]'
        )
      );

      this.autoplayEnabled =
        section.dataset.autoplay === 'true';

      this.autoplaySpeed =
        parseInt(
          section.dataset.autoplaySpeed,
          10
        ) || 2000;

      this.currentIndex = 0;

      this.timer = null;

      this.hasOverflow = false;

      this.resizeTimer = null;

      this.init();
    }


    /* ==========================================
       INIT
    ========================================== */

    init() {
      if (
        !this.viewport ||
        !this.track ||
        this.items.length === 0
      ) {
        return;
      }

      this.measure();

      this.bindEvents();

      this.updateDots();

      this.startAutoplay();
    }


    /* ==========================================
       MEASURE
    ========================================== */

    measure() {
      /*
       * Primero eliminamos la clase para medir
       * el ancho natural del contenido.
       */
      this.track.classList.remove(
        'is-overflowing'
      );

      requestAnimationFrame(() => {
        const trackWidth =
          this.getItemsTotalWidth();

        const viewportWidth =
          this.viewport.clientWidth;

        this.hasOverflow =
          trackWidth > viewportWidth + 2;


        /*
         * Si todos los logos caben:
         * quedan perfectamente centrados.
         *
         * Esto funciona para:
         * 1 logo
         * 2 logos
         * 3 logos
         * etc.
         */
        if (this.hasOverflow) {
          this.track.classList.add(
            'is-overflowing'
          );
        } else {
          this.track.classList.remove(
            'is-overflowing'
          );

          this.viewport.scrollTo({
            left: 0,
            behavior: 'auto'
          });

          this.currentIndex = 0;

          this.updateDots();
        }

        this.updateNavigationVisibility();
      });
    }


    /* ==========================================
       TOTAL WIDTH
    ========================================== */

    getItemsTotalWidth() {
      if (this.items.length === 0) {
        return 0;
      }

      let total = 0;

      this.items.forEach((item) => {
        total += item.offsetWidth;
      });

      if (this.items.length > 1) {
        const styles =
          window.getComputedStyle(
            this.track
          );

        const gap =
          parseFloat(styles.columnGap) ||
          parseFloat(styles.gap) ||
          0;

        total +=
          gap *
          (this.items.length - 1);
      }

      return total;
    }


    /* ==========================================
       EVENTS
    ========================================== */

    bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener(
          'click',
          () => {
            this.previous();

            this.restartAutoplay();
          }
        );
      }


      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          () => {
            this.next();

            this.restartAutoplay();
          }
        );
      }


      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => {
            const index =
              parseInt(
                dot.dataset.pdLogoDot,
                10
              );

            this.goTo(index);

            this.restartAutoplay();
          }
        );
      });


      this.section.addEventListener(
        'mouseenter',
        () => {
          this.stopAutoplay();
        }
      );


      this.section.addEventListener(
        'mouseleave',
        () => {
          this.startAutoplay();
        }
      );


      this.section.addEventListener(
        'focusin',
        () => {
          this.stopAutoplay();
        }
      );


      this.section.addEventListener(
        'focusout',
        () => {
          this.startAutoplay();
        }
      );


      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.stopAutoplay();
          } else {
            this.startAutoplay();
          }
        }
      );


      window.addEventListener(
        'resize',
        () => {
          clearTimeout(
            this.resizeTimer
          );

          this.resizeTimer =
            setTimeout(() => {
              this.measure();
            }, 150);
        }
      );
    }


    /* ==========================================
       NEXT
    ========================================== */

    next() {
      if (
        this.items.length <= 1 ||
        !this.hasOverflow
      ) {
        return;
      }

      let nextIndex =
        this.currentIndex + 1;

      if (
        nextIndex >=
        this.items.length
      ) {
        nextIndex = 0;
      }

      this.goTo(nextIndex);
    }


    /* ==========================================
       PREVIOUS
    ========================================== */

    previous() {
      if (
        this.items.length <= 1 ||
        !this.hasOverflow
      ) {
        return;
      }

      let previousIndex =
        this.currentIndex - 1;

      if (previousIndex < 0) {
        previousIndex =
          this.items.length - 1;
      }

      this.goTo(
        previousIndex
      );
    }


    /* ==========================================
       GO TO
    ========================================== */

    goTo(index) {
      if (
        !this.items[index]
      ) {
        return;
      }

      this.currentIndex =
        index;


      /*
       * Si todos los logos caben,
       * permanecen centrados.
       */
      if (!this.hasOverflow) {
        this.updateDots();

        return;
      }


      const item =
        this.items[index];


      const itemCenter =
        item.offsetLeft +
        item.offsetWidth / 2;


      const viewportCenter =
        this.viewport.clientWidth / 2;


      let target =
        itemCenter -
        viewportCenter;


      const maxScroll =
        this.viewport.scrollWidth -
        this.viewport.clientWidth;


      target =
        Math.max(
          0,
          Math.min(
            target,
            maxScroll
          )
        );


      this.viewport.scrollTo({
        left: target,
        behavior: 'smooth'
      });


      this.updateDots();
    }


    /* ==========================================
       DOTS
    ========================================== */

    updateDots() {
      if (
        this.dots.length === 0
      ) {
        return;
      }

      this.dots.forEach(
        (dot, index) => {
          const active =
            index ===
            this.currentIndex;

          dot.classList.toggle(
            'is-active',
            active
          );

          dot.setAttribute(
            'aria-current',
            active
              ? 'true'
              : 'false'
          );
        }
      );
    }


    /* ==========================================
       NAV VISIBILITY
    ========================================== */

    updateNavigationVisibility() {
      const navigation =
        this.section.querySelector(
          '[data-pd-logo-navigation]'
        );

      /*
       * Si existe un solo logo o todos caben,
       * no tiene sentido mostrar navegación.
       */
      if (navigation) {
        navigation.hidden =
          !this.hasOverflow ||
          this.items.length <= 1;
      }


      const dotsContainer =
        this.section.querySelector(
          '[data-pd-logo-dots]'
        );

      if (dotsContainer) {
        dotsContainer.hidden =
          !this.hasOverflow ||
          this.items.length <= 1;
      }
    }


    /* ==========================================
       AUTOPLAY
    ========================================== */

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        !this.hasOverflow ||
        this.items.length <= 1 ||
        document.hidden
      ) {
        return;
      }

      this.stopAutoplay();

      this.timer =
        window.setInterval(
          () => {
            this.next();
          },
          this.autoplaySpeed
        );
    }


    stopAutoplay() {
      if (!this.timer) {
        return;
      }

      window.clearInterval(
        this.timer
      );

      this.timer = null;
    }


    restartAutoplay() {
      this.stopAutoplay();

      this.startAutoplay();
    }
  }


  /* ==========================================
     INITIALIZE
  ========================================== */

  const initialize =
    (scope = document) => {
      const sections =
        scope.querySelectorAll(
          '[data-pd-logo-carousel]'
        );

      sections.forEach(
        (section) => {
          if (
            section.dataset
              .pdLogoInitialized ===
            'true'
          ) {
            return;
          }

          section.dataset
            .pdLogoInitialized =
            'true';

          new PDLogoCarousel(
            section
          );
        }
      );
    };


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initialize();
      }
    );
  } else {
    initialize();
  }


  /*
   * Shopify Theme Editor:
   * inicializa nuevamente la sección
   * cuando Shopify la recarga dinámicamente.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialize(
        event.target
      );
    }
  );
})();